import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeName } from '../styles/colors';
import { setTheme as setThemeUtil, getCurrentTheme } from '../utils/themeInit';

interface ThemeContextType {
  theme: ThemeName;
  toggleTheme: () => void;
  setTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    // 初期化時にDOMから現在のテーマを取得
    return getCurrentTheme();
  });

  useEffect(() => {
    // システムのテーマ変更を監視
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // localStorageに保存されたテーマがない場合のみ、システム設定に追従
      const savedTheme = localStorage.getItem('marle-library-theme');
      if (!savedTheme) {
        const newTheme: ThemeName = e.matches ? 'dark' : 'light';
        setThemeState(newTheme);
        setThemeUtil(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const newTheme: ThemeName = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);
    setThemeUtil(newTheme);
  };

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
    setThemeUtil(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

